import { User } from '@/models/UserModels';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import ApiService from '@/services/ApiService';
import { ApiKey, AuthRequest, AuthResponse, CreateApiKeyRequest, CreateApiKeyResponse, RefreshTokenRequest } from '@/models/AuthModels';
import { useCommonStore } from '@/stores/commonStore';
import { useUserStore } from '@/stores/userStore';

export const useAuthStore: any = defineStore('authStore', () => {
  const commonStore = useCommonStore();
  const userStore = useUserStore();

  const user = ref<User>(sessionStorage.getItem('user') != null ? JSON.parse(sessionStorage.user) : new User());
  const refreshToken = ref<string | null>(sessionStorage.getItem('refreshToken'));
  const token = ref<string | null>(sessionStorage.getItem('token'));
  const isAuthenticated = computed(() => !!token.value);
  const apiKeys = ref<Array<ApiKey>>([]);
  const createdApiKey = ref<CreateApiKeyResponse | null>(null);

  //#region AUTH
  async function login(data: AuthRequest): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, authLoading: true, authErrorMsg: '' });

    return ApiService.login(data)
      .then((res) => {
        const data: AuthResponse = res.data;

        user.value = data.user;
        token.value = data.token;
        refreshToken.value = data.refreshToken;

        sessionStorage.setItem('user', JSON.stringify(user.value));
        sessionStorage.setItem('token', token.value);
        sessionStorage.setItem('refreshToken', refreshToken.value);
      })
      .catch((err) => {
        console.error(err);
        commonStore.status.authErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.authLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function register(data: AuthRequest): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, registerUserLoading: true, registerUserErrorMsg: '' });

    return ApiService.register(data)
      .then((res) => {
        const data: User = res.data;

        // add user to the list of users in userStore
        userStore.addUserToList(data);
      })
      .catch((err) => {
        console.error(err);
        commonStore.status.registerUserErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.registerUserLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function refreshLogin(): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, authLoading: false, authErrorMsg: '' });

    return ApiService.refreshLogin(new RefreshTokenRequest(refreshToken.value!))
      .then((res) => {
        const data: AuthResponse = res.data;

        token.value = data.token;
        refreshToken.value = data.refreshToken;

        sessionStorage.setItem('token', token.value);
        sessionStorage.setItem('refreshToken', refreshToken.value);
      })
      .catch((err) => {
        console.error(err);
        commonStore.status.authErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.authLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function logout(): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, authLoading: true, authErrorMsg: '' });

    return ApiService.logout(new RefreshTokenRequest(refreshToken.value!))
      .then(() => {
        user.value = new User();
        token.value = null;
        refreshToken.value = null;

        sessionStorage.clear();
      })
      .catch((err) => {
        console.error(err);
        commonStore.status.authErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.authLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }
  //#endregion

  //#region API KEYS
  async function getAllApiKeys(): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, getApiKeysLoading: true, apiKeysErrorMsg: '' });

    return ApiService.getAllApiKeys()
      .then((res) => {
        apiKeys.value = res.data;
      })
      .catch((err) => {
        commonStore.status.apiKeysErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.getApiKeysLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  async function createApiKey(data: CreateApiKeyRequest): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, createApiKeysLoading: true, apiKeysErrorMsg: '' });

    return ApiService.createApiKey(data)
      .then((res) => {
        createdApiKey.value = res.data;

        if (createdApiKey.value != null) {
          const newKey: ApiKey = {
            id: createdApiKey.value?.id,
            name: createdApiKey.value?.name,
            description: createdApiKey.value?.description,
            createdAt: createdApiKey.value?.createdAt,
            lastUsedAt: createdApiKey.value?.lastUsedAt,
            expiresAt: createdApiKey.value?.expiresAt,
            isActive: createdApiKey.value?.isActive,
            createdBy: createdApiKey.value?.createdBy,
          };

          apiKeys.value.push(newKey);
        }
      })
      .catch((err) => {
        commonStore.status.apiKeysErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.createApiKeysLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  function resetCreatedApiKey(): void {
    createdApiKey.value = null;
  }

  async function deleteApiKey(id: number): Promise<void> {
    commonStore.setStatus({ ...commonStore.status, deleteApiKeysLoading: true, apiKeysErrorMsg: '' });

    return ApiService.deleteApiKey(id)
      .then(() => {
        apiKeys.value = apiKeys.value.filter((key) => key.id !== id);
      })
      .catch((err) => {
        commonStore.status.apiKeysErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.deleteApiKeysLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }
  //#endregion

  return {
    user,
    token,
    refreshToken,
    isAuthenticated,

    login,
    register,
    refreshLogin,
    logout,

    apiKeys,
    createdApiKey,
    getAllApiKeys,
    createApiKey,
    resetCreatedApiKey,
    deleteApiKey,
  };
});
